(() => {
  if (window.__monishaSmartPairingsLoaded) return;
  window.__monishaSmartPairingsLoaded = true;

  const pairingConfigurations = new WeakMap();

  /*
  ------------------------------------------------------------
  BASIC UI HELPERS
  ------------------------------------------------------------
  */

  function setMessage(container, message = "", type = "") {
    const el = container?.querySelector(
      "[data-smart-pairings-message]"
    );

    if (!el) return;

    el.textContent = message;
    el.hidden = !message;
    el.dataset.type = type;
  }

  function setStatus(item, message = "") {
    const el = item?.querySelector(
      "[data-smart-pairing-status]"
    );

    if (!el) return;

    el.textContent = message;
    el.hidden = !message;
  }

  function getCheckbox(item) {
    return item.querySelector(
      "[data-smart-pairing-checkbox]"
    );
  }

  function requiresConfiguration(item) {
    return (
      item.dataset.requiresConfiguration === "true"
    );
  }

  function createDefaultConfiguration(item) {
    const variantId = item.dataset.variantId;

    if (!variantId) {
      return null;
    }

    const formData = new FormData();

    formData.set("id", variantId);
    formData.set("quantity", "1");

    return formData;
  }

  function getMainForm(container) {
    const formId =
      container?.dataset.mainFormId;

    return formId
      ? document.getElementById(formId)
      : null;
  }

  /*
  ------------------------------------------------------------
  SMART PAIRINGS ANALYTICS
  ------------------------------------------------------------

  Publishes non-personal customer interaction events.

  It NEVER sends:
  - customer names
  - email addresses
  - personalisation messages
  - uploaded images/files

  Only product/rank/action information.
  ------------------------------------------------------------
  */

  function publishSmartPairingEvent(
    eventName,
    item,
    extra = {}
  ) {
    if (!item) return;

    const container =
      item.closest("[data-smart-pairings]");

    const mainForm =
      container
        ? getMainForm(container)
        : null;

    let mainVariantId = null;

    if (mainForm) {
      mainVariantId =
        new FormData(mainForm).get("id");
    }

    const payload = {
      sourceProductId:
        container?.dataset.sourceProductId ||
        null,

      sourceVariantId:
        mainVariantId
          ? String(mainVariantId)
          : null,

      pairingProductId:
        item.dataset.productId ||
        null,

      pairingVariantId:
        item.dataset.variantId ||
        null,

      pairingRank:
        item.dataset.rank ||
        null,

      pairingHandle:
        item.dataset.handle ||
        null,

      ...extra,
    };

    try {
      const publish =
        window.Shopify?.analytics?.publish;

      if (typeof publish !== "function") {
        return;
      }

      const result = publish(
        `monisha_smart_pairings:${eventName}`,
        payload
      );

      /*
        Analytics must never be allowed to
        break the buying experience.
      */
      if (
        result &&
        typeof result.catch === "function"
      ) {
        result.catch(() => {});
      }
    } catch (_) {
      /*
        Intentionally ignored.
      */
    }
  }

  /*
  ------------------------------------------------------------
  DYNAMIC CHECKOUT BUTTON
  ------------------------------------------------------------

  Shopify's dynamic payment button is hidden while
  Smart Pairings are selected because Smart Pairings
  needs to add the recommendation before Prestige
  submits the main product.
  ------------------------------------------------------------
  */

  function updateDynamicCheckout(container) {
    const form =
      getMainForm(container);

    if (!form) return;

    const hasSelectedPairing = Boolean(
      container.querySelector(
        "[data-smart-pairing-checkbox]:checked"
      )
    );

    const paymentButton =
      form.querySelector(
        ".shopify-payment-button"
      );

    if (paymentButton) {
      paymentButton.style.display =
        hasSelectedPairing
          ? "none"
          : "";
    }
  }

  /*
  ------------------------------------------------------------
  GLOBO PERSONALISATION
  ------------------------------------------------------------
  */

  async function loadGloboPersonalisation(
    item,
    modal,
    form
  ) {
    /*
      Only initialise Globo when the recommendation
      has been identified as personalised.
    */
    if (
      item.dataset.globoPersonalisation !== "true"
    ) {
      return true;
    }

    /*
      Never initialise Globo twice on the same
      Prestige popup form.

      This also helps prevent duplicate PMC
      Preview Personalisation buttons.
    */
    if (
      form.dataset.smartPairingGloboLoaded ===
        "true" ||
      form.querySelector(".gpo-container")
    ) {
      return true;
    }

    const handle =
      modal.getAttribute("handle") ||
      item.dataset.handle;

    if (!handle) {
      throw new Error(
        "Personalised product handle is missing."
      );
    }

    const gpo =
      window.GPOConfigs;

    if (
      !gpo ||
      typeof gpo.loadMainScripts !== "function"
    ) {
      throw new Error(
        "Globo Product Options is not ready."
      );
    }

    const response = await fetch(
      `${window.Shopify.routes.root}products/${encodeURIComponent(
        handle
      )}.js`
    );

    if (!response.ok) {
      throw new Error(
        `Could not load personalised product (${response.status}).`
      );
    }

    const pairingProduct =
      await response.json();

    await gpo.loadMainScripts(
      pairingProduct,
      gpo.customer,
      form
    );

    if (
      !form.querySelector(".gpo-container")
    ) {
      throw new Error(
        "Globo personalisation options were not returned."
      );
    }

    form.dataset.smartPairingGloboLoaded =
      "true";

    return true;
  }
    /*
  ------------------------------------------------------------
  SMART PAIRINGS QUICK VIEW LAYOUT
  ------------------------------------------------------------

  Presentation only.

  Does not modify:
  - Prestige image controls
  - gallery dots
  - variants
  - Globo fields
  - PMC
  - product form submission
  ------------------------------------------------------------
  */

  function applySmartPairingQuickViewLayout(
    modal,
    form
  ) {
    if (!modal || !form) {
      return;
    }

    /*
      Prestige's actual quick-buy structure:

      .quick-buy-modal__content
        ├── .quick-buy-modal__gallery-wrapper
        └── .quick-buy-modal__info-wrapper
    */

    const layout =
      modal.querySelector(
        ".quick-buy-modal__content"
      );

    const media =
      modal.querySelector(
        ".quick-buy-modal__gallery-wrapper"
      );

    const details =
      modal.querySelector(
        ".quick-buy-modal__info-wrapper"
      );

    const gallery =
      media?.querySelector(
        "product-gallery"
      );

    if (
      !layout ||
      !media ||
      !details ||
      !gallery ||
      !details.contains(form)
    ) {
      console.warn(
        "[Smart Pairings] Prestige quick-view layout could not be identified."
      );

      return;
    }

    /*
      Clean up the class accidentally applied
      to body by the previous layout attempt.
    */
    document.body.classList.remove(
      "smart-pairings-qv__shell"
    );

    modal.classList.add(
      "smart-pairings-qv"
    );

    layout.classList.add(
      "smart-pairings-qv__layout"
    );

    media.classList.add(
      "smart-pairings-qv__media"
    );

    details.classList.add(
      "smart-pairings-qv__details"
    );

    gallery.classList.add(
      "smart-pairings-qv__gallery"
    );
  }

     /*
  ------------------------------------------------------------
  QUICK VIEW / READ MORE
  ------------------------------------------------------------
  */

  async function prepareModal(item) {
    const container =
      item.closest("[data-smart-pairings]");

    const modal =
      item.querySelector("quick-buy-modal");

    if (!modal) {
      setMessage(
        container,
        "Quick view could not be opened.",
        "error"
      );

      return false;
    }

    await customElements.whenDefined(
      "quick-buy-modal"
    );

    if (
      typeof modal.show !== "function"
    ) {
      setMessage(
        container,
        "Quick view is not available on this product.",
        "error"
      );

      return false;
    }

    try {
      setMessage(container);

      await modal.show();
    } catch (error) {
      console.error(
        "[Smart Pairings] Quick view failed:",
        error
      );

      setMessage(
        container,
        "Quick view could not be loaded. Please try again.",
        "error"
      );

      return false;
    }

    const form =
      modal.querySelector(
        'form[is="product-form"]'
      );

    if (!form) {
      setMessage(
        container,
        "The product options could not be loaded.",
        "error"
      );

      return false;
    }

    /*
      Load Globo before marking the modal as
      prepared so personalised options exist
      before Select this product is used.
    */
    try {
      await loadGloboPersonalisation(
        item,
        modal,
        form
      );
    } catch (error) {
      console.error(
        "[Smart Pairings] Globo personalisation failed:",
        error
      );

      setMessage(
        container,
        "The personalisation options could not be loaded. Please try again.",
        "error"
      );

      return false;
    }
        /*
      Globo has now finished injecting any
      personalisation controls.

      Apply the layout only after the complete
      right-hand form exists.
    */
    applySmartPairingQuickViewLayout(
      modal,
      form
    );

    /*
      The modal may be opened repeatedly.
      Never attach the submit handler twice.
    */
    if (
      form.dataset.smartPairingPrepared ===
      "true"
    ) {
      return true;
    }

    form.dataset.smartPairingPrepared =
      "true";

    /*
      Dynamic checkout should not be available
      inside the Smart Pairings quick view.
    */
    const paymentButton =
      form.querySelector(
        ".shopify-payment-button"
      );

    if (paymentButton) {
      paymentButton.style.display =
        "none";
    }

    /*
      Convert Prestige's normal Add to Basket
      button into a configuration/select button.
    */
    const submitButton =
      form.querySelector(
        'button[type="submit"]'
      );

    if (submitButton) {
      submitButton.textContent =
        "Select this product";
    }

    /*
      IMPORTANT:
      The modal DOES NOT add the product to the
      basket.

      It captures all its FormData so we can
      preserve:
      - Globo fields
      - personalisation
      - file uploads
      - variant
      - quantity
    */
    form.addEventListener(
      "submit",
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        const formData =
          new FormData(form);

        const variantId =
          formData.get("id");

        if (!variantId) {
          setMessage(
            container,
            "Please choose an available option.",
            "error"
          );

          return;
        }

        if (
          !formData.has("quantity")
        ) {
          formData.set(
            "quantity",
            "1"
          );
        }

        pairingConfigurations.set(
          item,
          formData
        );

        item.dataset.variantId =
          String(variantId);

        item.classList.add(
          "is-configured"
        );

        const checkbox =
          getCheckbox(item);

        if (checkbox) {
          checkbox.checked = true;
        }

        setStatus(
          item,
          "Selected ✓"
        );

        publishSmartPairingEvent(
          "selected",
          item,
          {
            method: "quick_view",
          }
        );

        setMessage(container);

        updateDynamicCheckout(
          container
        );

        if (
          typeof modal.hide === "function"
        ) {
          modal.hide();
        }
      },
      true
    );

    return true;
  }

  async function openPairingModal(item) {
    return prepareModal(item);
  }

  /*
  ------------------------------------------------------------
  READ MORE CLICK
  ------------------------------------------------------------
  */

  document.addEventListener(
    "click",
    async (event) => {
      const button =
        event.target.closest(
          "[data-smart-pairing-read-more]"
        );

      if (!button) return;

      event.preventDefault();

      const item =
        button.closest(
          "[data-smart-pairing]"
        );

      if (!item) return;

      publishSmartPairingEvent(
        "read_more",
        item
      );

      await openPairingModal(item);
    }
  );

  /*
  ------------------------------------------------------------
  CHECKBOX SELECTION
  ------------------------------------------------------------
  */

  document.addEventListener(
    "change",
    async (event) => {
      const checkbox =
        event.target.closest(
          "[data-smart-pairing-checkbox]"
        );

      if (!checkbox) return;

      const item =
        checkbox.closest(
          "[data-smart-pairing]"
        );

      const container =
        checkbox.closest(
          "[data-smart-pairings]"
        );

      if (
        !item ||
        !container
      ) {
        return;
      }

      setMessage(container);

      /*
        Customer removes a pairing selection.
      */
      if (!checkbox.checked) {
        pairingConfigurations.delete(
          item
        );

        item.classList.remove(
          "is-configured"
        );

        setStatus(item);

        publishSmartPairingEvent(
          "unselected",
          item,
          {
            method: "checkbox",
          }
        );

        updateDynamicCheckout(
          container
        );

        return;
      }

      /*
        Personalised/option products must first
        be configured in the quick view.
      */
      if (
        requiresConfiguration(item) &&
        !pairingConfigurations.has(item)
      ) {
        checkbox.checked = false;

        await openPairingModal(item);

        if (
          !pairingConfigurations.has(item)
        ) {
          setMessage(
            container,
            "Please choose the options/personalisation for this product before adding it.",
            "info"
          );
        }

        updateDynamicCheckout(
          container
        );

        return;
      }

      /*
        Simple products can be selected directly
        from the checkbox using their default
        variant.
      */
      if (
        !pairingConfigurations.has(item)
      ) {
        const configuration =
          createDefaultConfiguration(
            item
          );

        if (!configuration) {
          checkbox.checked = false;

          setMessage(
            container,
            "This recommendation could not be selected.",
            "error"
          );

          return;
        }

        pairingConfigurations.set(
          item,
          configuration
        );
      }

      item.classList.add(
        "is-configured"
      );

      setStatus(
        item,
        "Selected ✓"
      );

      publishSmartPairingEvent(
        "selected",
        item,
        {
          method: "checkbox",
        }
      );

      updateDynamicCheckout(
        container
      );
    }
  );

  /*
  ------------------------------------------------------------
  ADD ONE SMART PAIRING TO SHOPIFY CART
  ------------------------------------------------------------
  */

  async function addPairingToCart(
    item,
    mainVariantId
  ) {
    const stored =
      pairingConfigurations.get(item);

    if (!stored) {
      throw new Error(
        "A selected recommendation has not been configured."
      );
    }

    /*
      Clone the stored FormData.

      Never modify the original configuration
      because it may contain:
      - Globo properties
      - file uploads
      - personalisation text
      - option selections
    */
    const formData =
      new FormData();

    for (
      const [key, value]
      of stored.entries()
    ) {
      formData.append(
        key,
        value
      );
    }

    if (
      !formData.has("quantity")
    ) {
      formData.set(
        "quantity",
        "1"
      );
    }

    /*
      These belong to Prestige's main product
      submission and should not be submitted
      with the separate Smart Pairing request.
    */
    formData.delete("sections");
    formData.delete("sections_url");
    formData.delete("on_success");

    /*
    ----------------------------------------------------------
    PRIVATE SMART PAIRINGS ORDER DATA
    ----------------------------------------------------------

    These allow:

    1. The 5% Shopify Function to identify
       Smart Pairings.

    2. The discount Function to verify that
       the source product is still in the cart.

    3. Future recommendation analytics to know
       which recommendation generated a purchase.
    ----------------------------------------------------------
    */

    formData.set(
      "properties[_Smart Pairing]",
      "Yes"
    );

    formData.set(
      "properties[_Smart Pairing Source]",
      String(
        mainVariantId || ""
      )
    );

    const pairingContainer =
      item.closest(
        "[data-smart-pairings]"
      );

    formData.set(
      "properties[_Smart Pairing Source Product]",
      String(
        pairingContainer
          ?.dataset
          .sourceProductId || ""
      )
    );

    formData.set(
      "properties[_Smart Pairing Rank]",
      String(
        item.dataset.rank || ""
      )
    );

    /*
      Add ONLY the recommended product.
    */
    const response =
      await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: "POST",
          body: formData,

          headers: {
            "X-Requested-With":
              "XMLHttpRequest",
          },
        }
      );

    const responseJson =
      await response.json();

    if (!response.ok) {
      throw new Error(
        responseJson.description ||
        responseJson.message ||
        "A recommendation could not be added to the basket."
      );
    }

    /*
      The pairing has genuinely reached Shopify's
      basket successfully.
    */
    publishSmartPairingEvent(
      "added_to_cart",
      item
    );

    return responseJson;
  }

  /*
  ------------------------------------------------------------
  MAIN PRODUCT FORM
  ------------------------------------------------------------

  Smart Pairings intercepts the FIRST submission only
  when recommendations have been selected.

  Flow:

      Smart Pairings recommendations
              ↓
      /cart/add.js
              ↓
      set bypass
              ↓
      requestSubmit()
              ↓
      Prestige submits MAIN product normally
              ↓
      Prestige cart drawer/events remain intact
  ------------------------------------------------------------
  */

  document.addEventListener(
    "submit",
    async (event) => {
      const form =
        event.target;

      if (
        !(
          form instanceof
          HTMLFormElement
        )
      ) {
        return;
      }

      if (
        form.getAttribute("is") !==
        "product-form"
      ) {
        return;
      }

      /*
        Smart Pairings deliberately submits this
        form a second time after its products have
        been added.

        On that second submission we MUST let
        Prestige handle everything.
      */
      if (
        form.dataset
          .smartPairingsBypass ===
        "true"
      ) {
        delete form.dataset
          .smartPairingsBypass;

        return;
      }

      /*
        IMPORTANT:
        Do NOT use form.id here.

        Prestige's product form contains an input
        named "id", which can shadow the form.id
        JavaScript property.

        getAttribute("id") gives us the real
        HTML form ID.
      */
      const actualFormId =
        form.getAttribute("id");

      if (!actualFormId) {
        return;
      }

      const container =
        document.querySelector(
          `[data-smart-pairings][data-main-form-id="${CSS.escape(
            actualFormId
          )}"]`
        );

      if (!container) {
        return;
      }

      const selectedItems =
        Array.from(
          container.querySelectorAll(
            "[data-smart-pairing]"
          )
        ).filter(
          (item) =>
            getCheckbox(item)?.checked
        );

      /*
        No recommendations selected:
        Smart Pairings does absolutely nothing.

        Prestige therefore behaves exactly as
        it normally would.
      */
      if (
        selectedItems.length === 0
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      /*
        Make sure every selected recommendation
        has valid FormData BEFORE we add anything
        to the basket.
      */
      for (
        const item of selectedItems
      ) {
        if (
          pairingConfigurations.has(
            item
          )
        ) {
          continue;
        }

        /*
          Personalised products should never
          reach this point unconfigured.
        */
        if (
          requiresConfiguration(item)
        ) {
          setMessage(
            container,
            "Please configure each selected personalised or option product first.",
            "error"
          );

          await openPairingModal(
            item
          );

          return;
        }

        /*
          Simple product fallback.
        */
        const configuration =
          createDefaultConfiguration(
            item
          );

        if (!configuration) {
          setMessage(
            container,
            "A selected recommendation is unavailable.",
            "error"
          );

          return;
        }

        pairingConfigurations.set(
          item,
          configuration
        );
      }

      const mainVariantId =
        new FormData(form).get(
          "id"
        );

      const submitter =
        event.submitter;

      const submitButtons =
        Array.from(
          form.elements
        ).filter(
          (element) =>
            element.type ===
            "submit"
        );

      /*
        Prevent double-clicking while Smart
        Pairings performs its basket additions.
      */
      submitButtons.forEach(
        (button) => {
          button.setAttribute(
            "disabled",
            "disabled"
          );

          button.setAttribute(
            "aria-busy",
            "true"
          );
        }
      );

      document.documentElement.dispatchEvent(
        new CustomEvent(
          "theme:loading:start",
          {
            bubbles: true,
          }
        )
      );

      try {
        /*
          Add ONLY the selected recommendations
          ourselves.

          The main product is left entirely to
          Prestige.
        */
        for (
          const item
          of selectedItems
        ) {
          await addPairingToCart(
            item,
            mainVariantId
          );
        }

        /*
          When Prestige confirms the MAIN product
          was successfully added, clear the
          Smart Pairings selections.
        */
        form.addEventListener(
          "variant:add",
          () => {
            selectedItems.forEach(
              (item) => {
                const checkbox =
                  getCheckbox(item);

                if (checkbox) {
                  checkbox.checked =
                    false;
                }

                pairingConfigurations.delete(
                  item
                );

                item.classList.remove(
                  "is-configured"
                );

                setStatus(item);
              }
            );

            setMessage(container);

            updateDynamicCheckout(
              container
            );
          },
          {
            once: true,
          }
        );

        /*
          Re-enable Prestige's submit button
          before handing the submission back.
        */
        submitButtons.forEach(
          (button) => {
            button.removeAttribute(
              "disabled"
            );

            button.removeAttribute(
              "aria-busy"
            );
          }
        );

        document.documentElement.dispatchEvent(
          new CustomEvent(
            "theme:loading:end",
            {
              bubbles: true,
            }
          )
        );

        /*
          SECOND SUBMISSION.

          Smart Pairings ignores this because
          of the bypass flag.

          Prestige receives it normally and
          handles:
          - main product
          - cart drawer
          - bundled sections
          - variant:add
          - cart:change
        */
        form.dataset
          .smartPairingsBypass =
          "true";

        if (
          submitter &&
          submitter instanceof HTMLElement &&
          submitter.form === form
        ) {
          form.requestSubmit(
            submitter
          );
        } else {
          form.requestSubmit();
        }
      } catch (error) {
        console.error(
          "[Smart Pairings] Add to basket failed:",
          error
        );

        submitButtons.forEach(
          (button) => {
            button.removeAttribute(
              "disabled"
            );

            button.removeAttribute(
              "aria-busy"
            );
          }
        );

        document.documentElement.dispatchEvent(
          new CustomEvent(
            "theme:loading:end",
            {
              bubbles: true,
            }
          )
        );

        setMessage(
          container,
          error?.message ||
            "We could not add your selected recommendations.",
          "error"
        );

        form.dispatchEvent(
          new CustomEvent(
            "cart:error",
            {
              bubbles: true,

              detail: {
                error:
                  error?.message ||
                  "We could not add your selected recommendations.",
              },
            }
          )
        );
      }
    },
    true
  );

  /*
  ------------------------------------------------------------
  IMPRESSION / SHOWN EVENTS
  ------------------------------------------------------------

  Record each recommendation visible when this
  Smart Pairings script first loads.
  ------------------------------------------------------------
  */

  document
    .querySelectorAll(
      "[data-smart-pairings] [data-smart-pairing]"
    )
    .forEach((item) => {
      publishSmartPairingEvent(
        "shown",
        item
      );
    });
})();